import { useMutation, useQueryClient } from '@tanstack/react-query';
import partition from 'lodash/partition';

import { apiClient } from '@cast/infrastructure-api';
import { RebalancingJob } from '@cast/types';

import { getRebalancingSchedulesQueryKey } from 'hooks/queries/scheduled-rebalancing/useRebalancingSchedulesQuery';

type UseAssignClusterScheduleMutationProps = {
  onSuccess?: (allClustersCount: number, assignedCount: number) => void;
  onError?: () => void;
};

type AssignClusterSchedule = {
  clusterId: string;
  scheduleId: string;
  enabled?: boolean;
};

export type BatchAssignScheduleArgs = {
  jobsToDelete?: RebalancingJob[];
  jobsToCreate?: AssignClusterSchedule[];
  jobsToEnable?: RebalancingJob[];
};

const createJob = ({
  clusterId,
  scheduleId,
  enabled = true,
}: AssignClusterSchedule) =>
  apiClient.scheduledRebalancing.createJob({
    clusterId,
    job: {
      clusterId,
      rebalancingScheduleId: scheduleId,
      enabled,
    },
  });

const deleteJob = (rebalancingJob: RebalancingJob) =>
  apiClient.scheduledRebalancing.deleteJob({
    clusterId: rebalancingJob.clusterId!,
    id: rebalancingJob.id!,
  });

const enableJob = (rebalancingJob: RebalancingJob) =>
  apiClient.scheduledRebalancing.updateJob({
    clusterId: rebalancingJob.clusterId!,
    id: rebalancingJob.id!,
    job: { ...rebalancingJob, enabled: true },
  });

export const useClusterScheduleAssignmentsMutation = ({
  onSuccess,
  onError,
}: UseAssignClusterScheduleMutationProps = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobsToCreate,
      jobsToEnable,
      jobsToDelete,
    }: BatchAssignScheduleArgs) => {
      const fns: Array<() => Promise<unknown>> = [];
      if (jobsToCreate?.length) {
        for (const j of jobsToCreate) {
          fns.push(() => createJob(j));
        }
      }
      if (jobsToDelete?.length) {
        for (const j of jobsToDelete) {
          fns.push(() => deleteJob(j));
        }
      }
      if (jobsToEnable?.length) {
        for (const j of jobsToEnable) {
          fns.push(() => enableJob(j));
        }
      }
      return Promise.allSettled(fns.map((fn) => fn()));
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({
        queryKey: getRebalancingSchedulesQueryKey(),
      });
      const [fulfilled] = partition(
        results,
        (entry) => entry.status === 'fulfilled'
      );
      onSuccess?.(results.length, fulfilled.length);
    },
    onError,
  });
};
